import { type ReactNode, useRef, useState } from "react";
import { generatePath, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { ChevronLeft, ChevronGrabberVertical } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { FilterPopover } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { ShellCloseButton } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { TITLE_PILL_MAX_WIDTH, TitleText } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { useProductDrill } from "./ProductDrillContext";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";

const DrillSelectTrigger = styled(Pill)(({ theme }) => ({
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
  maxWidth: TITLE_PILL_MAX_WIDTH,
  margin: 0,
  cursor: "pointer",
  "& svg": {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    color: theme.fieldSalesTwo.colors.neutral[700],
  },
  ...glassCapsulePress(theme, { restShadow: theme.fieldSalesTwo.shadows.glass }),
}));

// TitleText brings the ellipsis but sizes itself in TitlePill's wrappers; inside the
// flex trigger it needs minWidth to actually shrink.
const DrillSelectLabel = styled(TitleText)({
  minWidth: 0,
});

// Same glass capsule and width clamp as the clickable trigger, minus the chevron
// and press affordance.
const DrillStaticPill = styled(Pill)(({ theme }) => ({
  minWidth: 0,
  maxWidth: TITLE_PILL_MAX_WIDTH,
  margin: 0,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
}));

function DrillBackButton() {
  const { stack, drillTo } = useProductDrill();
  const t = useTranslations("fieldSalesTwo.customerNav");

  return (
    <HeaderIconButton type="button" aria-label={t("back")} onClick={() => drillTo(stack.length - 1)}>
      <ChevronLeft />
    </HeaderIconButton>
  );
}

function DrillPathSelect({ rootJumpLabel }: AnalysisDrillNavProps): ReactNode {
  const { stack, drillTo } = useProductDrill();
  const t = useTranslations("fieldSalesTwo.analysis");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const currentGroup = stack.at(-1);
  if (!currentGroup) return null;

  // First level down: show the group's title as a plain pill. The clickable path
  // select (chevron + jump-back popover) only makes sense once there is an
  // ancestor to return to, i.e. from the second level onward.
  if (stack.length < 2)
    return (
      <DrillStaticPill variant="title">
        <DrillSelectLabel>{currentGroup.name}</DrillSelectLabel>
      </DrillStaticPill>
    );

  // Item keys encode the depth to keep when jumping. The view title leads in its own
  // group (FilterPopover separates groups with a rule) and jumps back to the root.
  const pathGroups = [
    { key: "root", items: [{ key: "0", label: rootJumpLabel ?? t("title") }] },
    {
      key: "path",
      items: stack.map((level, index) => ({ key: String(index + 1), label: level.name })),
    },
  ];

  return (
    <>
      <DrillSelectTrigger
        as="button"
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <DrillSelectLabel>{currentGroup.name}</DrillSelectLabel>
        <ChevronGrabberVertical />
      </DrillSelectTrigger>
      <FilterPopover
        anchorRef={triggerRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        align="start"
        groups={pathGroups}
        selectedKeys={[String(stack.length)]}
        onToggle={(key) => {
          drillTo(Number(key));
          setOpen(false);
        }}
        ariaLabel={t("productGroups")}
      />
    </>
  );
}

interface AnalysisDrillNavProps {
  /** Label of the path popover's jump-to-root entry. Defaults to the analysis title. */
  rootJumpLabel?: string;
}

export function AnalysisDrillNav({ rootJumpLabel }: AnalysisDrillNavProps = {}): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { stack } = useProductDrill();

  // At the drill root this is a first-layer page: keep the X, but close back to the customer overview.
  // Once drilled into a product group the back arrow ascends one level instead.
  if (stack.length === 0)
    return <ShellCloseButton to={id ? generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }) : undefined} />;
  return (
    <>
      <DrillBackButton />
      <DrillPathSelect rootJumpLabel={rootJumpLabel} />
    </>
  );
}
